import React, { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ColDef, GridReadyEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-alpine.css';

interface PlayerTeamStatsTableProps {
  teamName: string;
  teamCode: number;
  playerStats: Array<{ player_name: string; position: string; stats: Record<string, number> }>;
}

const PlayerTeamStatsTable: React.FC<PlayerTeamStatsTableProps> = ({ teamName, teamCode, playerStats }) => {
  const rowData = useMemo(() => {
    return playerStats.map(player => ({
      Player: player.player_name,
      Position: player.position,
      ...player.stats
    }));
  }, [playerStats]);

  const statKeys = useMemo(() => {
    if (rowData.length === 0) return [];
    return Object.keys(rowData[0]).filter(key => !["Player", "Position"].includes(key));
  }, [rowData]);

  const formatNumericValue = (value: any): string | number => {
  if (value === null || value === undefined) return '-';
  if (typeof value !== 'number') return value;
  
  if (Number.isInteger(value)) {
    return value;
  }
  
  return parseFloat(value.toFixed(2));
};

const columnDefs: ColDef[] = useMemo(() => [
  { 
    headerName: "Player", 
    field: "Player", 
    sortable: true, 
    filter: true, 
    width: 150,
    pinned: 'left'
  },
  { 
    headerName: "Position", 
    field: "Position", 
    sortable: true, 
    filter: true, 
    width: 150,
    pinned: 'left'
  },
  ...statKeys.map(key => ({ 
    headerName: key.replace(/_/g, ' ').toUpperCase(), 
    field: key, 
    sortable: true, 
    filter: true, 
    width: 150,
    valueFormatter: (params: any) => {
      return formatNumericValue(params.value);
    }
  })),
], [statKeys]);

  const logoUrl = `https://premier.athlytesports.com/assets/images/teams/${teamCode}.png`;

  return (
    <div className="p-4 bg-white shadow-md rounded-md mb-6">
      <div className="flex items-center mb-4">
        <img src={logoUrl} alt={teamName} className="w-6 h-6 mr-2" onError={e => {(e.target as HTMLImageElement).style.display='none';}} />
        <span className="font-semibold text-lg">{teamName}</span>
      </div>
      <div className="ag-theme-alpine w-full" style={{ height: '450px' }}>
        <AgGridReact<any>
          modules={[AllCommunityModule]}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{ 
            sortable: true, 
            filter: true, 
            resizable: true,
            suppressSizeToFit: true
          }}
          suppressHorizontalScroll={false}
          domLayout="normal"
        />
      </div>
    </div>
  );
};

export default PlayerTeamStatsTable;